import React, { useEffect, useState } from "react";
import { getCreditAnalysisPortfolioReview } from "../../../../shared/services/apis/tenantAnalytics";
import IframeViewer from "../../../../shared/ui/urlOpener/IFrameViewer";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  Document,
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
  AlignmentType,
  BorderStyle,
  HeadingLevel,
  Packer,
  convertInchesToTwip,
  TableLayoutType,
  PageOrientation,
  Footer,
  ImageRun,
} from "docx";
import { saveAs } from "file-saver";
import { RiDownloadLine } from "react-icons/ri";
import { getTenantById } from "../../../../shared/services/apis/tenant";

interface Tenant {
  id: string;
  name: string;
  logoUrl?: string;
}

interface PortfolioReviewProps {
  tenantId: string;
  tenantServiceId: string;
  tenantServiceOptionId: string;
}

export const PortfolioReview: React.FC<PortfolioReviewProps> = ({
  tenantId,
  tenantServiceId,
  tenantServiceOptionId,
}) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadingWord, setDownloadingWord] = useState(false);
  const [tenant, setTenant] = useState<Tenant | null>();

  const handleSourceClick = (e: React.MouseEvent, url: string) => {
    e.preventDefault();
    setIframeUrl(url);
  };

  useEffect(() => {
    if (!tenantId) return;

    const fetchTenant = async () => {
      try {
        const data = await getTenantById(tenantId);
        setTenant(data);
      } catch (error) {
        console.error("Error fetching tenant:", error);
      }
    };

    fetchTenant();
  }, [tenantId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await getCreditAnalysisPortfolioReview(
          tenantId,
          tenantServiceId,
          tenantServiceOptionId
        );
        setData(response);
        setError(null);
      } catch (err) {
        setError("Failed to load portfolio review");
      } finally {
        setLoading(false);
      }
    };
    if (tenantId && tenantServiceId && tenantServiceOptionId) fetchData();
  }, [tenantId, tenantServiceId, tenantServiceOptionId]);

  const downloadPDF = async () => {
    setDownloading(true);

    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const portfolio = data?.content?.portfolio_review_management;
      const loadImage = (url: string): Promise<HTMLImageElement> => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = "Anonymous";
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = url;
        });
      };

      // Load watermark image if logoUrl exists
      let watermarkImg: HTMLImageElement | null = null;
      if (tenant?.logoUrl) {
        try {
          watermarkImg = await loadImage(tenant.logoUrl);
        } catch (error) {
          console.warn("Failed to load watermark image:", error);
        }
      }

      // Function to add watermark to a page
      const addWatermark = (pageNumber: number) => {
        if (!watermarkImg) return;

        doc.setPage(pageNumber);

        // Save graphics state and set transparency
        doc.saveGraphicsState();
        doc.setGState(doc.GState({ opacity: 0.1 })); // Remove 'new' keyword

        // Calculate watermark size and position (centered)
        const watermarkWidth = 80;
        const watermarkHeight =
          (watermarkImg.height / watermarkImg.width) * watermarkWidth;
        const xPos = (pageWidth - watermarkWidth) / 2;
        const yPos = (pageHeight - watermarkHeight) / 2;

        // Add watermark image
        doc.addImage(
          watermarkImg,
          "PNG",
          xPos,
          yPos,
          watermarkWidth,
          watermarkHeight
        );

        // Restore graphics state
        doc.restoreGraphicsState();
      };
      // Header
      doc.setFontSize(18);
      doc.setFont("helvetica", "bold");
      doc.text("Portfolio Review & Management", pageWidth / 2, 20, {
        align: "center",
      });

      // Date and time
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100, 100, 100);
      const currentDateTime = new Date().toLocaleString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
      doc.text(`Generated on ${currentDateTime}`, pageWidth / 2, 28, {
        align: "center",
      });

      let yPosition = 40;

      // Account Status Section
      if (portfolio?.account_status) {
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 0, 0);
        doc.text("Account Status", 14, yPosition);
        yPosition += 8;

        const accountStatusData = [
          [
            "Current Classification",
            portfolio.account_status.current_classification || "N/A",
          ],
          ["NPA Status", portfolio.account_status.NPA_status || "N/A"],
        ];

        autoTable(doc, {
          startY: yPosition,
          head: [["Parameter", "Value"]],
          body: accountStatusData,
          theme: "grid",
          styles: { fontSize: 9, cellPadding: 3 },
          headStyles: {
            fillColor: [243, 244, 246],
            textColor: [55, 65, 81],
            fontStyle: "bold",
          },
          margin: { left: 14, right: 14 },
        });

        yPosition = (doc as any).lastAutoTable.finalY + 10;

        // Early Warning Signals
        if (portfolio.account_status.early_warning_signals) {
          doc.setFontSize(11);
          doc.setFont("helvetica", "bold");
          doc.text("Early Warning Signals", 14, yPosition);
          yPosition += 8;

          const warningSignals = Object.entries(
            portfolio.account_status.early_warning_signals
          )
            .filter(([_, val]) => val)
            .map(([key, val]: [string, any]) => {
              // const parts = val.split(";");
              // const firstPart = parts[0]?.replace(/;/g, " ").trim();

              return [
                key
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (l: string) => l.toUpperCase()),
                val,
              ];
            });

          autoTable(doc, {
            startY: yPosition,
            head: [["Parameter", "Value"]],
            body: warningSignals,
            theme: "grid",
            styles: { fontSize: 8, cellPadding: 2 },
            headStyles: {
              fillColor: [243, 244, 246],
              textColor: [55, 65, 81],
              fontStyle: "bold",
            },
            margin: { left: 14, right: 14 },
          });

          yPosition = (doc as any).lastAutoTable.finalY + 10;
        }
      }

      // Footer
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        addWatermark(i);
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 10, {
          align: "center",
        });
      }

      const fileName = `Portfolio_Review_${new Date().getTime()}.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to generate PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const downloadWord = async () => {
    setDownloadingWord(true);

    try {
      // Helper function to convert base64 to Uint8Array
      const base64ToUint8Array = (base64: string): Uint8Array => {
        const binaryString = window.atob(base64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes;
      };

      // Load logo image if logoUrl exists
      let logoImageData: {
        data: Uint8Array;
        mimeType: "jpg" | "png" | "gif" | "bmp";
      } | null = null;
      if (tenant?.logoUrl) {
        try {
          console.log("Loading logo from:", tenant.logoUrl);
          const response = await fetch(tenant.logoUrl);
          const blob = await response.blob();

          // Map MIME type to docx accepted format
          const getMimeType = (
            blobType: string
          ): "jpg" | "png" | "gif" | "bmp" => {
            if (blobType.includes("png")) return "png";
            if (blobType.includes("jpeg") || blobType.includes("jpg"))
              return "jpg";
            if (blobType.includes("gif")) return "gif";
            if (blobType.includes("bmp")) return "bmp";
            return "png";
          };

          const mimeType = getMimeType(blob.type);
          console.log("Detected MIME type:", mimeType);

          const base64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              const result = reader.result as string;
              const base64Data = result.split(",")[1];
              resolve(base64Data);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });

          const uint8Array = base64ToUint8Array(base64);
          console.log("Logo loaded, size:", uint8Array.length, "bytes");

          logoImageData = { data: uint8Array, mimeType };
        } catch (error) {
          console.error("Failed to load logo image:", error);
        }
      }

      const portfolio = data?.content?.portfolio_review_management;
      const children: (Paragraph | Table)[] = [];

      const pageWidth = convertInchesToTwip(8.5);
      const marginLeft = convertInchesToTwip(0.75);
      const marginRight = convertInchesToTwip(0.75);
      const availableWidth = pageWidth - marginLeft - marginRight;

      // Header
      children.push(
        new Paragraph({
          text: "Portfolio Review & Management",
          heading: HeadingLevel.TITLE,
          alignment: AlignmentType.CENTER,
          spacing: { after: 200, before: 200 },
        })
      );

      const currentDateTime = new Date().toLocaleString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });

      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: `Generated on ${currentDateTime}`,
              size: 20,
              color: "666666",
              italics: true,
            }),
          ],
          alignment: AlignmentType.CENTER,
          spacing: { after: 600 },
        })
      );

      // Account Status
      if (portfolio?.account_status) {
        children.push(
          new Paragraph({
            text: "Account Status",
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 400, after: 200 },
          })
        );

        const col1Width = availableWidth * 0.4;
        const col2Width = availableWidth * 0.6;

        const accountStatusRows = [
          new TableRow({
            children: [
              new TableCell({
                children: [
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: "Parameter",
                        bold: true,
                        size: 20,
                        color: "FFFFFF",
                      }),
                    ],
                  }),
                ],
                shading: { fill: "4472C4" },
                width: { size: col1Width, type: WidthType.DXA },
                margins: {
                  top: convertInchesToTwip(0.08),
                  bottom: convertInchesToTwip(0.08),
                  left: convertInchesToTwip(0.08),
                  right: convertInchesToTwip(0.08),
                },
              }),
              new TableCell({
                children: [
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: "Value",
                        bold: true,
                        size: 20,
                        color: "FFFFFF",
                      }),
                    ],
                  }),
                ],
                shading: { fill: "4472C4" },
                width: { size: col2Width, type: WidthType.DXA },
                margins: {
                  top: convertInchesToTwip(0.08),
                  bottom: convertInchesToTwip(0.08),
                  left: convertInchesToTwip(0.08),
                  right: convertInchesToTwip(0.08),
                },
              }),
            ],
            tableHeader: true,
          }),
        ];

        const accountItems = [
          {
            label: "Current Classification",
            value: portfolio.account_status.current_classification || "N/A",
          },
          {
            label: "NPA Status",
            value: portfolio.account_status.NPA_status || "N/A",
          },
        ];

        accountItems.forEach((item, idx) => {
          accountStatusRows.push(
            new TableRow({
              children: [
                new TableCell({
                  children: [
                    new Paragraph({
                      text: item.label,
                      spacing: { after: 80, before: 80 },
                    }),
                  ],
                  shading: { fill: idx % 2 === 0 ? "FFFFFF" : "F8F9FA" },
                  width: { size: col1Width, type: WidthType.DXA },
                  margins: {
                    top: convertInchesToTwip(0.08),
                    bottom: convertInchesToTwip(0.08),
                    left: convertInchesToTwip(0.08),
                    right: convertInchesToTwip(0.08),
                  },
                }),
                new TableCell({
                  children: [
                    new Paragraph({
                      text: item.value,
                      spacing: { after: 80, before: 80 },
                    }),
                  ],
                  shading: { fill: idx % 2 === 0 ? "FFFFFF" : "F8F9FA" },
                  width: { size: col2Width, type: WidthType.DXA },
                  margins: {
                    top: convertInchesToTwip(0.08),
                    bottom: convertInchesToTwip(0.08),
                    left: convertInchesToTwip(0.08),
                    right: convertInchesToTwip(0.08),
                  },
                }),
              ],
            })
          );
        });

        const accountStatusTable = new Table({
          rows: accountStatusRows,
          width: { size: availableWidth, type: WidthType.DXA },
          columnWidths: [col1Width, col2Width],
          layout: TableLayoutType.FIXED,
          borders: {
            top: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
            bottom: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
            left: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
            right: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
            insideHorizontal: {
              style: BorderStyle.SINGLE,
              size: 1,
              color: "DDDDDD",
            },
            insideVertical: {
              style: BorderStyle.SINGLE,
              size: 1,
              color: "DDDDDD",
            },
          },
        });

        children.push(accountStatusTable);
        children.push(new Paragraph({ text: "", spacing: { after: 200 } }));

        // Early Warning Signals
        if (portfolio.account_status.early_warning_signals) {
          children.push(
            new Paragraph({
              text: "Early Warning Signals",
              heading: HeadingLevel.HEADING_3,
              spacing: { before: 400, after: 200 },
            })
          );

          const warningRows = [
            new TableRow({
              children: [
                new TableCell({
                  children: [
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: "Parameter",
                          bold: true,
                          size: 20,
                          color: "FFFFFF",
                        }),
                      ],
                    }),
                  ],
                  shading: { fill: "4472C4" },
                  width: { size: col1Width, type: WidthType.DXA },
                  margins: {
                    top: convertInchesToTwip(0.08),
                    bottom: convertInchesToTwip(0.08),
                    left: convertInchesToTwip(0.08),
                    right: convertInchesToTwip(0.08),
                  },
                }),
                new TableCell({
                  children: [
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: "Value",
                          bold: true,
                          size: 20,
                          color: "FFFFFF",
                        }),
                      ],
                    }),
                  ],
                  shading: { fill: "4472C4" },
                  width: { size: col2Width, type: WidthType.DXA },
                  margins: {
                    top: convertInchesToTwip(0.08),
                    bottom: convertInchesToTwip(0.08),
                    left: convertInchesToTwip(0.08),
                    right: convertInchesToTwip(0.08),
                  },
                }),
              ],
              tableHeader: true,
            }),
          ];

          const warningSignals = Object.entries(
            portfolio.account_status.early_warning_signals
          ).filter(([_, val]) => val);

          warningSignals.forEach(([key, val]: [string, any], idx) => {
            const label = key
              .replace(/_/g, " ")
              .replace(/\b\w/g, (l: string) => l.toUpperCase());

            warningRows.push(
              new TableRow({
                children: [
                  new TableCell({
                    children: [
                      new Paragraph({
                        text: label,
                        spacing: { after: 80, before: 80 },
                      }),
                    ],
                    shading: { fill: idx % 2 === 0 ? "FFFFFF" : "F8F9FA" },
                    width: { size: col1Width, type: WidthType.DXA },
                    margins: {
                      top: convertInchesToTwip(0.08),
                      bottom: convertInchesToTwip(0.08),
                      left: convertInchesToTwip(0.08),
                      right: convertInchesToTwip(0.08),
                    },
                  }),
                  new TableCell({
                    children: [
                      new Paragraph({
                        text: val,
                        spacing: { after: 80, before: 80 },
                      }),
                    ],
                    shading: { fill: idx % 2 === 0 ? "FFFFFF" : "F8F9FA" },
                    width: { size: col2Width, type: WidthType.DXA },
                    margins: {
                      top: convertInchesToTwip(0.08),
                      bottom: convertInchesToTwip(0.08),
                      left: convertInchesToTwip(0.08),
                      right: convertInchesToTwip(0.08),
                    },
                  }),
                ],
              })
            );
          });

          const warningTable = new Table({
            rows: warningRows,
            width: { size: availableWidth, type: WidthType.DXA },
            columnWidths: [col1Width, col2Width],
            layout: TableLayoutType.FIXED,
            borders: {
              top: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
              bottom: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
              left: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
              right: { style: BorderStyle.SINGLE, size: 3, color: "CCCCCC" },
              insideHorizontal: {
                style: BorderStyle.SINGLE,
                size: 1,
                color: "DDDDDD",
              },
              insideVertical: {
                style: BorderStyle.SINGLE,
                size: 1,
                color: "DDDDDD",
              },
            },
          });

          children.push(warningTable);
          children.push(new Paragraph({ text: "", spacing: { after: 200 } }));
        }
      }

      // Create footer with logo in right bottom corner
      const footerChildren: Paragraph[] = [];

      if (logoImageData) {
        footerChildren.push(
          new Paragraph({
            children: [
              new ImageRun({
                type: logoImageData.mimeType,
                data: logoImageData.data,
                transformation: {
                  width: 60,
                  height: 60,
                },
              }),
            ],
            alignment: AlignmentType.RIGHT,
            spacing: { before: 100 },
          })
        );
      }

      // Create Document
      const doc = new Document({
        creator: "Portfolio Analysis System",
        title: "Portfolio Review & Management",
        sections: [
          {
            properties: {
              page: {
                margin: {
                  top: convertInchesToTwip(0.75),
                  bottom: convertInchesToTwip(0.75),
                  left: convertInchesToTwip(0.75),
                  right: convertInchesToTwip(0.75),
                },
                size: {
                  width: convertInchesToTwip(8.5),
                  height: convertInchesToTwip(11),
                  orientation: PageOrientation.PORTRAIT,
                },
              },
            },
            footers: {
              default: new Footer({
                children: footerChildren,
              }),
            },
            children: children,
          },
        ],
      });

      const blob = await Packer.toBlob(doc);
      const fileName = `Portfolio_Review_${new Date().getTime()}.docx`;
      saveAs(blob, fileName);
    } catch (error) {
      console.error("Error generating Word document:", error);
      alert("Failed to generate Word document. Please try again.");
    } finally {
      setDownloadingWord(false);
    }
  };

  if (loading)
    return <div className="h-64 bg-gray-200 rounded animate-pulse" />;

  if (error)
    return (
      <div className="max-w-4xl mx-auto mt-8">
        <div className="bg-gray-50 border border-gray-300 rounded-xl p-6 shadow-lg">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Error Loading Data
              </h3>
              <p className="text-gray-700 text-sm leading-relaxed">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );

  const portfolio = data?.content?.portfolio_review_management;

  return (
    <div className="bg-white p-6 rounded-2xl shadow border border-gray-300 max-w-4xl mx-auto">
      {/* Header with Download Buttons */}
      <div className="pb-6 border-b border-gray-300 mb-6">
        <div className="flex items-center justify-between">
          <h3 className="text-2xl text-gray-700 font-light">
            Portfolio Review & Management
          </h3>

          <div className="flex gap-2">
            <button
              onClick={downloadPDF}
              disabled={downloading}
              className="flex items-center gap-2 hover:bg-gray-200 font-light px-4 py-2 rounded-full border border-gray-300 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {downloading ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Generating...
                </>
              ) : (
                <>
                  <RiDownloadLine className="w-4 h-4" />
                  PDF
                </>
              )}
            </button>

            <button
              onClick={downloadWord}
              disabled={downloadingWord}
              className="flex items-center gap-2 hover:bg-gray-200 font-light px-4 py-2 rounded-full border border-gray-300 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {downloadingWord ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Generating...
                </>
              ) : (
                <>
                  <RiDownloadLine className="w-4 h-4" />
                  Word
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Account Status */}
      {portfolio?.account_status && (
        <div className="mb-6 pb-6 border-b border-gray-300">
          <h4 className="text-md font-light text-gray-500 mb-4">
            Account Status
          </h4>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <p className="text-gray-500 font-medium mb-1 text-sm">
                Current Classification
              </p>
              <p className="text-md font-light text-gray-700">
                {portfolio.account_status.current_classification}
              </p>
            </div>
            <div>
              <p className="text-gray-500 font-medium mb-1 text-sm">
                NPA Status
              </p>
              <p className="text-md font-light text-gray-700">
                {portfolio.account_status.NPA_status || "N/A"}
              </p>
            </div>
          </div>
          {portfolio.account_status.early_warning_signals && (
            <div className="mt-4 p-2">
              <h5 className="text-sm font-medium text-gray-800 mb-3">
                Early Warning Signals
              </h5>
              <table className="min-w-full border border-gray-200 rounded-lg text-sm">
                <thead className="bg-gray-100 border-b border-gray-200">
                  <tr>
                    <th className="text-left py-2 px-3 font-semibold text-gray-700">
                      Parameter
                    </th>
                    <th className="text-left py-2 px-3 font-semibold text-gray-700">
                      Value
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(
                    Object.entries(
                      portfolio.account_status.early_warning_signals
                    ) as [string, string][]
                  ).map(([key, val]) => {
                    if (!val) return null;

                    const parts = val.split(";");
                    const firstPart = parts[0]?.replace(/;/g, " ").trim();
                    const secondPart = parts
                      .slice(1)
                      .join(" ")
                      .replace(/;/g, " ")
                      .trim();

                    return (
                      <tr
                        key={key}
                        className="border-b border-gray-200 last:border-0 hover:bg-gray-50 transition-colors"
                      >
                        <td className="py-2 px-3 text-gray-600 capitalize">
                          {key.replace(/_/g, " ")}
                        </td>
                        <td className="py-2 px-3 text-gray-800">
                          <div>{firstPart}</div>
                          {secondPart && (
                            <div className="text-gray-500 text-xs mt-1">
                              {secondPart}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {portfolio?.concentration_risk && (
        <div className="mb-6 pb-6 border-b border-gray-300">
          <h4 className="text-md font-light text-gray-500 mb-4">
            Concentration Risks
          </h4>
          <div className="bg-gray-50 rounded-lg p-4 overflow-x-auto">
            <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono">
              {JSON.stringify(
                portfolio.vintage_analysis_migration_reports_concentration_risk
                  .concentration_risk,
                null,
                2
              )}
            </pre>
          </div>
        </div>
      )}

      {/* Citations */}
      {data?.citations && data.citations.length > 0 && (
        <div className="ml-1 text-gray-700 max-w-4xl text-sm mt-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Sources</h4>
          <p className="mb-1">
            {data.citations.map((sourceUrl: string, i: number) => {
              const url = new URL(sourceUrl);
              const display = url.hostname.replace("www.", "");
              return (
                <button
                  key={i}
                  onClick={(e) => handleSourceClick(e, sourceUrl)}
                  className="text-xs text-gray-700 border ml-1 border-gray-300 font-medium px-2 py-1 rounded-full hover:bg-gray-50 cursor-pointer"
                >
                  {display}
                </button>
              );
            })}
          </p>
        </div>
      )}
      {iframeUrl && (
        <IframeViewer url={iframeUrl} onClose={() => setIframeUrl(null)} />
      )}
    </div>
  );
};
